/**
 * `/settings/members` — workspace members list + invite/remove.
 *
 * Replacement for legacy `MembersIsland.tsx`. Data: Clerk
 * `useOrganization({ memberships })` gives us per-user details
 * (email, avatar, role). The backend's `POST /v1/management/orgs/
 * {org_uuid}/members` requires a pre-minted user_uuid, so the
 * invite-by-email flow rides on Clerk directly
 * (`organization.inviteMember`) — the Supabase mirror is updated by
 * the Clerk webhook consumer when the member accepts.
 *
 * Role changes are done via Clerk today
 * (`membership.update({ role })`) because the backend doesn't
 * expose a PATCH yet (Gap M2).
 *
 * The global "Invite" action in the sidebar links to
 * `/settings/members?invite=1` — we open the invite dialog
 * automatically when that flag is present.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { PageActions } from '@/components/primitives/PageActions';
import { UpsellInline } from '@/components/primitives/UpsellInline';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { mapClerkError } from '@/lib/mapClerkError';
import { useEntitlement } from '@/lib/queries';
import { useOrganization, useUser } from '@clerk/react';
import type { OrganizationMembershipResource } from '@clerk/shared/types';
import { Plus, UserCirclePlus, Users } from '@phosphor-icons/react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useEffect, useMemo, useState } from 'react';

type MembersSearch = { invite?: string };

/**
 * Role label map for the role-select trigger. Clerk's role values
 * include `org:` prefixes on legacy tokens (`org:admin`) and a
 * snake_case literal (`basic_member`); both collapse to one of two
 * display labels. Used as the `SelectValue` render-prop child below
 * so the trigger never flashes the raw lowercase value on first paint
 * (Base UI's default behaviour until the popup mounts and the
 * matching SelectItem's ItemText registers).
 */
function roleLabel(value: unknown): string {
  if (value === 'admin' || value === 'org:admin') return 'Admin';
  if (value === 'basic_member' || value === 'member' || value === 'org:member') return 'Member';
  return typeof value === 'string' && value.length > 0
    ? value
        .replace(/^org:/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Member';
}

export const Route = createFileRoute('/_app/settings/members')({
  component: MembersPage,
  validateSearch: (raw: Record<string, unknown>): MembersSearch =>
    typeof raw.invite === 'string' && raw.invite.length > 0 ? { invite: raw.invite } : {},
});

function MembersPage(): ReactElement {
  const { organization, membership, memberships, isLoaded } = useOrganization({
    memberships: { infinite: false, pageSize: 100 },
  });
  const { user } = useUser();
  const navigate = useNavigate();
  const { invite } = useSearch({ from: '/_app/settings/members' });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [toRemove, setToRemove] = useState<OrganizationMembershipResource | null>(null);
  const [removing, setRemoving] = useState(false);

  // Auto-open the invite dialog when landed with `?invite=1`. Clear
  // the query param after we've consumed it so a refresh doesn't
  // re-trigger it.
  useEffect(() => {
    if (!invite) return;
    setInviteOpen(true);
    navigate({ to: '/settings/members', search: {}, replace: true });
  }, [invite, navigate]);

  const isAdmin = membership?.role === 'admin' || membership?.role === 'org:admin';

  // Pricing-tier gate. The free tier was removed (§9.24) so every
  // paid plan allows at least one seat beyond the owner; suspended
  // orgs get the upsell instead of a failed Clerk invite. Server-
  // side enforcement still happens via Clerk + our webhook mirror.
  const inviteEntitlement = useEntitlement('invite_member');
  const canInvite = isAdmin && inviteEntitlement.allowed;

  const sorted = useMemo(() => {
    const list = memberships?.data ?? [];
    return [...list].sort((a, b) => {
      // Admins first, then by name / email.
      const roleDelta = (a.role === 'admin' ? 0 : 1) - (b.role === 'admin' ? 0 : 1);
      if (roleDelta !== 0) return roleDelta;
      const an = a.publicUserData?.firstName ?? a.publicUserData?.identifier ?? '';
      const bn = b.publicUserData?.firstName ?? b.publicUserData?.identifier ?? '';
      return an.localeCompare(bn);
    });
  }, [memberships?.data]);

  async function onRemoveConfirm() {
    if (!toRemove) return;
    setRemoving(true);
    try {
      await toRemove.destroy();
      toastSuccess('Member removed');
      setToRemove(null);
      await memberships?.revalidate?.();
    } catch (err) {
      toastFromError(err);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Members</h2>
          <p className="text-sm text-[var(--color-mute)]">
            People with access to this workspace. Admins can invite, remove, and promote teammates.
          </p>
        </div>
        {isAdmin && (
          <PageActions>
            {canInvite ? (
              <Button onClick={() => setInviteOpen(true)}>
                <UserCirclePlus aria-hidden="true" />
                Invite
              </Button>
            ) : inviteEntitlement.isLoading ? null : (
              <UpsellInline variant="button" minPlan={inviteEntitlement.minPlan} />
            )}
          </PageActions>
        )}
      </header>

      {!isLoaded && (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )}

      {isLoaded && sorted.length === 0 && (
        <EmptyState
          icon={Users}
          title="Just you for now"
          description="Invite teammates to share audits and dashboards."
          action={
            !isAdmin ? undefined : canInvite ? (
              <Button onClick={() => setInviteOpen(true)}>
                <Plus aria-hidden="true" />
                Invite teammate
              </Button>
            ) : inviteEntitlement.isLoading ? undefined : (
              <UpsellInline variant="button" minPlan={inviteEntitlement.minPlan} />
            )
          }
        />
      )}

      {isLoaded && sorted.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line-soft)] text-left text-xs uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  <th className="px-4 py-2 font-medium">Member</th>
                  <th className="px-4 py-2 font-medium">Role</th>
                  <th className="px-4 py-2 font-medium">Joined</th>
                  <th className="w-16 px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => (
                  <MemberRow
                    key={m.id}
                    membership={m}
                    isSelf={m.publicUserData?.userId === user?.id}
                    isAdmin={isAdmin}
                    onRemove={() => setToRemove(m)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={async () => {
          await memberships?.revalidate?.();
        }}
      />

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(next) => {
          if (!next) setToRemove(null);
        }}
        title="Remove member?"
        description={
          toRemove
            ? `${toRemove.publicUserData?.identifier ?? 'This member'} will lose access to every audit, monitor, and API key in the workspace. This can't be undone.`
            : undefined
        }
        confirmLabel="Remove"
        destructive
        loading={removing}
        onConfirm={onRemoveConfirm}
      />
    </div>
  );
}

function MemberRow({
  membership,
  isSelf,
  isAdmin,
  onRemove,
}: {
  membership: OrganizationMembershipResource;
  isSelf: boolean;
  isAdmin: boolean;
  onRemove: () => void;
}): ReactElement {
  const [updating, setUpdating] = useState(false);
  const identifier = membership.publicUserData?.identifier ?? '—';
  const name = [membership.publicUserData?.firstName, membership.publicUserData?.lastName]
    .filter(Boolean)
    .join(' ');

  async function onRoleChange(next: string) {
    if (next === membership.role) return;
    setUpdating(true);
    try {
      await membership.update({ role: next });
      toastSuccess('Role updated');
    } catch (err) {
      toastFromError(err);
    } finally {
      setUpdating(false);
    }
  }

  return (
    <tr className="border-b border-[var(--color-line-soft)]/70">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--color-paper-alt)]">
            {membership.publicUserData?.imageUrl ? (
              <img
                src={membership.publicUserData.imageUrl}
                alt=""
                className="size-full object-cover"
              />
            ) : (
              <span className="text-sm font-medium text-[var(--color-ink-soft)]">
                {(name || identifier).slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-[var(--color-ink)]">
              {name || identifier}
              {isSelf && (
                <span className="ml-2 rounded-full bg-[var(--color-paper-alt)] px-1.5 py-0.5 text-xs font-normal text-[var(--color-ink-soft)]">
                  you
                </span>
              )}
            </div>
            <div className="truncate text-xs text-[var(--color-mute)]">{identifier}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {isAdmin && !isSelf ? (
          <Select
            value={membership.role}
            onValueChange={(v) => {
              if (typeof v === 'string') void onRoleChange(v);
            }}
            disabled={updating}
          >
            <SelectTrigger className="w-32">
              <SelectValue>{(v) => roleLabel(v)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="basic_member">Member</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className="text-sm capitalize text-[var(--color-ink-soft)]">
            {membership.role.replace(/^org:/, '').replace(/_/g, ' ')}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-[var(--color-mute)]">
        {new Date(membership.createdAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3 text-right">
        {isAdmin && !isSelf && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onRemove}
            aria-label={`Remove ${name || identifier}`}
          >
            Remove
          </Button>
        )}
      </td>
    </tr>
  );
}

function InviteDialog({
  open,
  onOpenChange,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onInvited: () => Promise<void>;
}): ReactElement {
  const { organization } = useOrganization();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('basic_member');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (open) return;
    setEmail('');
    setRole('basic_member');
    setFormError(undefined);
    setFieldErrors({});
  }, [open]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organization) return;
    setFormError(undefined);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await organization.inviteMember({
        emailAddress: email.trim(),
        role,
      });
      toastSuccess('Invitation sent', `${email.trim()} will receive an email shortly.`);
      await onInvited();
      onOpenChange(false);
    } catch (err) {
      const mapped = mapClerkError(err);
      setFieldErrors(mapped.fieldErrors);
      setFormError(mapped.formError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite a teammate</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <DialogPanel>
            <div className="space-y-4">
              <Field>
                <FieldLabel htmlFor="invite-email">Email</FieldLabel>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="teammate@example.com"
                  value={email}
                  onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                  autoFocus
                  required
                />
                {fieldErrors.emailAddress && <FieldError>{fieldErrors.emailAddress}</FieldError>}
              </Field>
              <Field>
                <FieldLabel htmlFor="invite-role">Role</FieldLabel>
                <Select
                  value={role}
                  onValueChange={(v) => {
                    if (typeof v === 'string') setRole(v);
                  }}
                >
                  <SelectTrigger id="invite-role">
                    <SelectValue>{(v) => roleLabel(v)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic_member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {formError && (
                <div
                  className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
                  role="alert"
                >
                  {formError}
                </div>
              )}
            </div>
          </DialogPanel>
          <DialogFooter>
            <DialogClose render={<Button variant="outline">Cancel</Button>} />
            <Button type="submit" loading={submitting}>
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
